import React, { useState, useEffect } from "react";
import axios from "axios";
import Papa from "papaparse";
import {
  Container,
  Typography,
  Button,
  Box,
  Snackbar,
  TextField,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  Tooltip,
} from "@mui/material";
import { Upload } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton } from "@mui/material";
import {
  AddCircle,
  Edit,
  AccountBalance,
  CheckCircle,
  Warning,
  ReceiptLong,
  ArrowForward,
} from "@mui/icons-material";
import DuplicateAlertDialog from "../components/DuplicateAlertDialog";
import ResponsiveAppBar from "../components/AppBar";
import { useNavigate } from "react-router-dom";
import { DeleteIcon } from "lucide-react";
import DeleteWithPassword from "../components/deleteWithPassword";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import RoleGuard from "../components/RoleGuard";
import { API_BASE_URL } from "../api/config";
import CustomerSearchBar, { matchCustomerSearch } from "../components/CustomerSearchBar";

const BASE_URL = `${API_BASE_URL}/velavan_finance`;

const initialEntryState = {
  refNo: "",
  date: "",
  name: "",
  location: "",
  aadharNo1: "",
  aadharNo2: "",
  contactNo1: "",
  contactNo2: "",
  occupation: "",
  lendedAmount: "",
  weeklyDue: "",
  pendingDue: "",
  userId: "",
  password: "",
};

// Create theme with specified color scheme
const theme = createTheme({
  palette: {
    primary: { main: '#77c8c7' }, // Teal
    secondary: { main: '#936097' }, // Plum
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    success: { main: '#77c8c7' },
    info: { main: '#936097' },
  },
  typography: {
    fontFamily: '"Outfit", "Poppins", "Roboto", sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.5px',
      color: '#1e293b',
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.3px',
      color: '#1e293b',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#64748b',
      fontSize: '1.05rem',
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 22px',
          boxShadow: '0 4px 12px rgba(147, 96, 151, 0.08)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(119, 200, 199, 0.25)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
          color: '#ffffff',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            background: '#ffffff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#77c8c7',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#936097',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(226, 232, 240, 0.9)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
          backgroundColor: '#ffffff',
          '& .MuiDataGrid-columnHeaders': {
            background: '#f8fafc',
            color: '#475569',
            fontWeight: 700,
          },
          '& .MuiDataGrid-cell': {
            borderColor: '#f1f5f9',
            color: '#334155',
          },
          '& .closed-row': {
            backgroundColor: 'rgba(254, 242, 242, 0.85) !important', // Light red for closed accounts
            color: '#991b1b',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(254, 226, 226, 0.95) !important',
            },
          },
          '& .npa-row': {
            backgroundColor: 'rgba(254, 243, 199, 0.45) !important',
            color: '#b45309',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(254, 243, 199, 0.7) !important',
            },
          },
          '& .active-row': {
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(119, 200, 199, 0.08) !important',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
          color: '#ffffff',
          fontWeight: 700,
          padding: '16px 24px',
        },
      },
    },
  },
});

const VelavanFinance = () => {
  const [entries, setEntries] = useState([]);
  const navigate = useNavigate();
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateEntry, setDuplicateEntry] = useState(null);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [originalRefNo, setOriginalRefNo] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [editingEntry, setEditingEntry] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newEntry, setNewEntry] = useState(initialEntryState);
  const [selectedPage, setSelectedPage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    if (openAddDialog) {
      const numbers = entries.map(entry => {
        const match = entry.refNo?.match(/VF-(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      });

      const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
      const nextNumber = (maxNumber + 1).toString().padStart(4, '0');
      const refNo = `VF-${nextNumber}`;
      const userId = `VF${nextNumber}`;
      const password = nextNumber;

      setNewEntry({
        ...initialEntryState,
        refNo,
        userId,
        password,
      });
    }
  }, [openAddDialog, entries]);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/get_all_entries`);
      setEntries(res.data.map((entry) => ({ ...entry, id: entry.refNo })));
    } catch (error) {
      console.error("Error fetching entries:", error);
      setSnackbarMessage("Error fetching entries.");
      setOpenSnackbar(true);
    }
  };

  const handleNavigate = (event) => {
    const page = event.target.value;
    setSelectedPage(page);
    if (page) {
      navigate(page);
    }
  };

  const getDisplayStatus = (value) => {
    const val = value?.toLowerCase();
    if (val === "active") return "Active";
    if (val === "closed/paid") return "Closed";
    if (val === "npa") return "NPA";
    return "Unknown";
  };

  // Summary Metrics
  const activeCount = entries.filter(e => e.accountStatus?.toLowerCase() === 'active').length;
  const closedCount = entries.filter(e => {
    const s = e.accountStatus?.toLowerCase();
    return s === 'closed/paid' || s === 'closed' || s === 'paid';
  }).length;
  const npaCount = entries.filter(e => e.accountStatus?.toLowerCase() === 'npa').length;
  const totalLended = entries.reduce((acc, curr) => acc + (parseFloat(curr.lendedAmount) || 0), 0);

  const columns = [
    {
      field: "addNew",
      headerName: "#",
      width: 80,
      sortable: false,
      renderHeader: () => (
        <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
          <Tooltip title="Add New Customer Record">
            <IconButton
              size="small"
              onClick={() => setOpenAddDialog(true)}
              sx={{ color: '#77c8c7', '&:hover': { bgcolor: 'rgba(119, 200, 199, 0.15)' } }}
            >
              <AddCircle fontSize="small" />
            </IconButton>
          </Tooltip>
        </RoleGuard>
      ),
      renderCell: (params) => {
        const visibleSortedRows = params.api.getSortedRowIds();
        const rowIndex = visibleSortedRows.indexOf(params.id) + 1;
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
            {rowIndex}
          </Typography>
        );
      },
    },
    {
      field: "refNo",
      headerName: "Ref No",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{ bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#0f766e', fontWeight: 700, borderRadius: 1.5 }}
        />
      )
    },
    { field: "date", headerName: "Date", width: 120 },
    {
      field: "name",
      headerName: "Customer Name",
      width: 160,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
          {params.value}
        </Typography>
      )
    },
    { field: "location", headerName: "Location", width: 140 },
    { field: "aadharNo1", headerName: "Aadhar No 1", width: 130 },
    { field: "aadharNo2", headerName: "Aadhar No 2", width: 130 },
    { field: "contactNo1", headerName: "Contact 1", width: 130 },
    { field: "contactNo2", headerName: "Contact 2", width: 130 },
    { field: "occupation", headerName: "Occupation", width: 140 },
    {
      field: 'lendedAmount',
      headerName: 'Lended Amount',
      minWidth: 150,
      type: 'number',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '0.875rem' }}>
          ₹{params.value ? Number(params.value).toLocaleString() : 0}
        </Typography>
      )
    },
    {
      field: 'weeklyDue',
      headerName: 'Due Amount',
      minWidth: 130,
      type: 'number',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#936097', fontSize: '0.875rem' }}>
          ₹{params.value ? Number(params.value).toLocaleString() : 0}
        </Typography>
      )
    },
    { field: "pendingDue", headerName: "Pending Due", width: 120 },
    { field: "userId", headerName: "User ID", width: 130 },
    { field: "password", headerName: "Password", width: 120 },
    {
      field: "accountStatus",
      headerName: "Status",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const status = params.value?.toLowerCase();
        let bg = "rgba(119, 200, 199, 0.15)";
        let text = "#0f766e";
        if (status === "closed/paid" || status === "closed" || status === "paid") {
          bg = "rgba(254, 226, 226, 0.9)";
          text = "#991b1b";
        } else if (status === "npa") {
          bg = "rgba(245, 158, 11, 0.15)";
          text = "#b45309";
        }

        return (
          <Chip
            label={getDisplayStatus(status)}
            size="small"
            sx={{
              bgcolor: bg,
              color: text,
              fontWeight: 700,
              borderRadius: 2,
              px: 1,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
            <Tooltip title="Edit Record">
              <IconButton
                size="small"
                onClick={() => handleEdit(params.row)}
                sx={{ color: '#77c8c7', '&:hover': { bgcolor: 'rgba(119, 200, 199, 0.15)' } }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </RoleGuard>
          <RoleGuard allowedRoles={["Admin Manager"]}>
            <DeleteWithPassword
              resourceId={params.row.refNo}
              resourceName="Entry"
              onDelete={async (refNo) => {
                try {
                  await axios.delete(`${BASE_URL}/delete_by_ref/${refNo}`);
                  fetchEntries();
                } catch (error) {
                  console.error("Error deleting entry:", error);
                  throw error;
                }
              }}
              navigate={navigate}
              renderTrigger={(onClick) => (
                <Tooltip title="Delete Record">
                  <IconButton
                    size="small"
                    sx={{ color: '#936097', '&:hover': { bgcolor: 'rgba(147, 96, 151, 0.12)' } }}
                    onClick={onClick}
                  >
                    <DeleteIcon size={18} />
                  </IconButton>
                </Tooltip>
              )}
            />
          </RoleGuard>
        </Box>
      ),
    },
  ];

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = ({ target }) => {
        const csvData = Papa.parse(target.result, { header: true, skipEmptyLines: true }).data;
        console.log("📂 Parsed CSV Data:", csvData);
        processCSVData(csvData);
      };
      reader.readAsText(file);
    }
  };

  const processCSVData = async (data) => {
    try {
      const existingEntriesRes = await axios.get(`${BASE_URL}/get_all_entries`);
      const existingEntries = existingEntriesRes.data;
      const existingAadharNumbers = new Set(existingEntries.flatMap(e => [e.aadharNo1?.trim(), e.aadharNo2?.trim()]));
      const existingContactNumbers = new Set(existingEntries.flatMap(e => [e.contactNo1?.trim(), e.contactNo2?.trim()]));
      let tempDuplicates = [];
      let uniqueEntries = [];
      data.forEach((entry, index) => {
        const newEntry = {
          id: entry.refNo?.trim() || `row-${index}`,
          refNo: entry.refNo?.trim() || "",
          name: entry.name?.trim() || "Unknown",
          location: entry.location?.trim() || "N/A",
          aadharNo1: entry.aadharNo1?.trim() || "N/A",
          aadharNo2: entry.aadharNo2?.trim() || "N/A",
          contactNo1: entry.contactNo1?.trim() || "N/A",
          contactNo2: entry.contactNo2?.trim() || "N/A",
          occupation: entry.occupation?.trim() || "N/A",
          date: typeof entry.date === "string" ? entry.date.trim() : "N/A",
          lendedAmount: parseFloat(entry.lendedAmount) || 0,
          weeklyDue: parseFloat(entry.weeklyDue) || 0,
          pendingDue: parseFloat(entry.pendingDue) || 0,
          accountStatus: entry.accountStatus
        };
        const isDuplicate =
          (newEntry.aadharNo1?.trim() && existingAadharNumbers.has(newEntry.aadharNo1.trim())) ||
          (newEntry.aadharNo2?.trim() && existingAadharNumbers.has(newEntry.aadharNo2.trim())) ||
          (newEntry.contactNo1?.trim() && existingContactNumbers.has(newEntry.contactNo1.trim())) ||
          (newEntry.contactNo2?.trim() && existingContactNumbers.has(newEntry.contactNo2.trim()));
        if (isDuplicate) {
          tempDuplicates.push(newEntry);
        } else {
          uniqueEntries.push(newEntry);
        }
      });
      if (tempDuplicates.length > 0) {
        setDuplicates(tempDuplicates);
        setOpenDialog(true);
        return;
      }
      if (uniqueEntries.length > 0) {
        await axios.post(`${BASE_URL}/add_bulk_entry`, uniqueEntries);
        fetchEntries();
        setSnackbarMessage("CSV uploaded successfully!");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("❌ CSV Processing Error:", error);
      setSnackbarMessage("Error processing CSV data. Please try again.");
      setOpenSnackbar(true);
    }
  };

  const handleAddEntry = async () => {
    try {
      const existingEntriesRes = await axios.get(`${BASE_URL}/get_all_entries`);
      const existingEntries = existingEntriesRes.data;

      let conflictEntry = null;

      for (const existing of existingEntries) {
        if (
          (newEntry.aadharNo1 && [existing.aadharNo1, existing.aadharNo2].includes(newEntry.aadharNo1.trim())) ||
          (newEntry.aadharNo2 && [existing.aadharNo1, existing.aadharNo2].includes(newEntry.aadharNo2.trim())) ||
          (newEntry.contactNo1 && [existing.contactNo1, existing.contactNo2].includes(newEntry.contactNo1.trim())) ||
          (newEntry.contactNo2 && [existing.contactNo1, existing.contactNo2].includes(newEntry.contactNo2.trim()))
        ) {
          conflictEntry = existing;
          break;
        }
      }

      if (conflictEntry) {
        setDuplicateEntry({
          ...newEntry,
          isExisting: true,
          existingRefNo: conflictEntry.refNo
        });
        setOpenDuplicateDialog(true);
        return;
      }

      await addEntryToDatabase(newEntry, false);
    } catch (error) {
      handleAddEntryError(error);
    }
  };

  const handleConfirmDuplicate = async (allowDuplicate) => {
    setOpenDuplicateDialog(false);
    if (allowDuplicate && duplicateEntry) {
      await addEntryToDatabase(duplicateEntry, true);
    }
  };

  const addEntryToDatabase = async (entry, allowDuplicate) => {
    try {
      await axios.post(`${BASE_URL}/add_single_entry`, { ...entry, allowDuplicate });
      setSnackbarMessage("Entry added successfully!");
      setOpenSnackbar(true);
      setOpenAddDialog(false);
      setNewEntry(initialEntryState);
      fetchEntries();
    } catch (error) {
      handleAddEntryError(error);
    }
  };

  const handleAddEntryError = (error) => {
    console.error("❌ Error adding entry:", error);
    setSnackbarMessage(error.response?.data?.error || "Failed to add entry.");
    setOpenSnackbar(true);
  };

  const handleDuplicateDialogClose = async (confirmUpload) => {
    setOpenDialog(false);
    if (confirmUpload) {
      try {
        await axios.post(`${BASE_URL}/add_bulk_entry/confirm`, duplicates);
        setSnackbarMessage("Duplicates uploaded successfully!");
        setOpenSnackbar(true);
        fetchEntries();
      } catch (error) {
        console.error("❌ Error uploading duplicates:", error);
        setSnackbarMessage("Failed to upload duplicates.");
        setOpenSnackbar(true);
      }
    } else {
      setSnackbarMessage("Upload cancelled. Duplicates found.");
      setOpenSnackbar(true);
    }
    setDuplicates([]);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setOriginalRefNo(entry.refNo);
    setOpenEditDialog(true);
  };

  const handleEditSubmit = async () => {
    try {
      await axios.put(`${BASE_URL}/update_by_ref/${originalRefNo}`, editingEntry);
      setSnackbarMessage("Entry updated successfully!");
      setOpenEditDialog(false);
      fetchEntries();
    } catch (error) {
      console.error("Error updating entry:", error);
      setSnackbarMessage("Failed to update entry.");
    }
    setOpenSnackbar(true);
  };

  const handleInputChange = (e) => {
    setEditingEntry({ ...editingEntry, [e.target.name]: e.target.value });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    accept: ".csv",
  });

  const filteredEntries = entries.filter((entry) => matchCustomerSearch(entry, searchQuery));

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    const numA = parseInt(a.refNo?.replace("VF-", "")?.trim() || 0, 10);
    const numB = parseInt(b?.refNo?.replace("VF-", "")?.trim() || 0, 10);
    return numA - numB;
  });

  return (
    <>
      <ResponsiveAppBar />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, md: 5 } }}>
          <Container maxWidth="xl">
            {/* Header Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
              <Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.6, borderRadius: '50px', bgcolor: 'rgba(119, 200, 199, 0.15)', mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#77c8c7' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#936097', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Core Finance Ledger
                  </Typography>
                </Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                  Velavan Finance Customer Directory
                </Typography>
                <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                  Centralized customer loan repository, bulk CSV upload & real-time accounts management
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
                  <Button
                    variant="contained"
                    startIcon={<AddCircle />}
                    onClick={() => setOpenAddDialog(true)}
                    sx={{
                      background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                      color: '#ffffff',
                      fontWeight: 700,
                    }}
                  >
                    Add Customer Entry
                  </Button>
                </RoleGuard>
              </Box>
            </Box>

            {/* Top KPI Metrics */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(119, 200, 199, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#77c8c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <AccountBalance sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Total Accounts
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        {entries.length} <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#64748b' }}>active & closed</span>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(34, 197, 94, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CheckCircle sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Active Customers
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        {activeCount} <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#22c55e' }}>running</span>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(245, 158, 11, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Warning sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        NPA / Overdue
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#b45309' }}>
                        {npaCount} <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#64748b' }}>accounts</span>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(147, 96, 151, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(147, 96, 151, 0.12)', color: '#936097', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ReceiptLong sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Total Lended Capital
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        ₹{totalLended.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Action Bar: CSV Drag & Drop + Navigate Selector */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 3,
                width: '100%',
              }}
            >
              <Box
                {...getRootProps()}
                sx={{
                  flex: { xs: '1 1 100%', md: '0 1 65%' },
                  border: '2px dashed rgba(119, 200, 199, 0.7)',
                  p: 2,
                  borderRadius: 3.5,
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.02)',
                  transition: 'all 0.3s ease-in-out',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(119, 200, 199, 0.05)',
                    borderColor: '#936097',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <input {...getInputProps()} />
                <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#77c8c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Upload size={20} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#334155',
                    fontWeight: 600,
                  }}
                >
                  Drag & Drop bulk <strong style={{ color: '#0f766e' }}>CSV files</strong> here, or <strong style={{ color: '#936097' }}>browse filesystem</strong> to upload
                </Typography>
              </Box>

              <FormControl
                size="small"
                sx={{
                  flex: { xs: '1 1 100%', md: '0 1 32%' },
                  minWidth: 200,
                  backgroundColor: '#ffffff',
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.02)',
                }}
              >
                <InputLabel sx={{ fontWeight: 500, color: '#64748b' }}>Navigate Module</InputLabel>
                <Select
                  value={selectedPage}
                  label="Navigate Module"
                  onChange={handleNavigate}
                  sx={{
                    borderRadius: 3,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(226, 232, 240, 0.9)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#77c8c7',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#936097',
                    }
                  }}
                >
                  <MenuItem value="/admin">Gold & Vehicle Loan</MenuItem>
                  <MenuItem value="/velavan-finance">Velavan Finance Ledger</MenuItem>
                  <MenuItem value="/due-matters">Due Matters</MenuItem>
                  <MenuItem value="/interestCalculation">Interest Calculation</MenuItem>
                  <MenuItem value="/dailyAccounts">Daily Accounts</MenuItem>
                  <MenuItem value="/finalAccounts">Final Accounts</MenuItem>
                  <MenuItem value="/monthly-graph">Monthly Graph Status</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Customer Search Card */}
            <CustomerSearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              totalCount={entries.length}
              filteredCount={sortedEntries.length}
              placeholder="Search by Refer ID (e.g. VF-0001), Customer Name, Contact, or Aadhaar No..."
              title="Velavan Finance Customer Search"
              subtitle="Instantly search across Refer ID, Customer Name, Phone & Aadhaar numbers in a single field"
            />

            <Box sx={{ height: 600, width: "100%" }}>
              <DataGrid
                rows={sortedEntries}
                columns={columns}
                checkboxSelection
                pagination
                getRowClassName={(params) => {
                  const status = params.row.accountStatus?.toLowerCase();
                  if (status === "closed/paid" || status === "closed" || status === "paid") {
                    return "closed-row";
                  }
                  if (status === "npa") {
                    return "npa-row";
                  }
                  return "active-row";
                }}
                sx={{
                  fontSize: { xs: "12px", md: "13.5px" },
                  '& .closed-row': {
                    backgroundColor: 'rgba(254, 242, 242, 0.85) !important',
                    color: '#991b1b',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(254, 226, 226, 0.95) !important',
                    },
                  },
                  '& .npa-row': {
                    backgroundColor: 'rgba(254, 243, 199, 0.45) !important',
                    color: '#b45309',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(254, 243, 199, 0.7) !important',
                    },
                  },
                  '& .active-row': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: 'rgba(119, 200, 199, 0.08) !important',
                    },
                  },
                  '& .MuiDataGrid-row': {
                    transition: 'background-color 0.15s',
                  },
                }}
              />
            </Box>

            {/* Add New Entry Dialog */}
            <Dialog
              open={openAddDialog}
              onClose={() => setOpenAddDialog(false)}
              fullWidth
              maxWidth="sm"
              PaperProps={{ sx: { borderRadius: 3.5, overflow: 'hidden' } }}
            >
              <DialogTitle>Add New Customer Entry</DialogTitle>
              <DialogContent sx={{ pt: 3, bgcolor: '#ffffff' }}>
                <Grid container spacing={2}>
                  {Object.keys(newEntry).map((key) => {
                    if (key === "refNo" || key === "userId" || key === "password") {
                      return (
                        <Grid item xs={12} sm={4} key={key}>
                          <TextField
                            margin="dense"
                            label={key === "refNo" ? "Reference No" : key === "userId" ? "User ID" : "Password"}
                            name={key}
                            fullWidth
                            value={newEntry[key] || ""}
                            disabled
                          />
                        </Grid>
                      );
                    }
                    if (key === "aadharNo1" || key === "aadharNo2") {
                      return (
                        <Grid item xs={12} sm={6} key={key}>
                          <TextField
                            margin="dense"
                            label={key === "aadharNo1" ? "Aadhar No 1" : "Aadhar No 2"}
                            name={key}
                            fullWidth
                            value={newEntry[key] || ""}
                            inputProps={{ maxLength: 14 }}
                            onChange={(e) => {
                              let value = e.target.value.replace(/\D/g, "");
                              value = value.slice(0, 12);
                              const formatted = value
                                .replace(/(\d{4})(\d{0,4})(\d{0,4})/, (match, p1, p2, p3) =>
                                  [p1, p2, p3].filter(Boolean).join(" ")
                                );
                              setNewEntry({ ...newEntry, [key]: formatted });
                            }}
                          />
                        </Grid>
                      );
                    }
                    if (key === "pendingDue") {
                      return (
                        <Grid item xs={12} sm={6} key={key}>
                          <TextField
                            margin="dense"
                            label="Pending Due"
                            name={key}
                            fullWidth
                            value={newEntry.pendingDue || ""}
                            disabled
                          />
                        </Grid>
                      );
                    }
                    if (key === "date") {
                      const formatDate = (value) => {
                        if (!value) return "";
                        const [day, month, year] = value.split("-");
                        return `${year}-${month}-${day}`;
                      };
                      const unformatDate = (value) => {
                        if (!value) return "";
                        const [year, month, day] = value.split("-");
                        return `${day}-${month}-${year}`;
                      };
                      return (
                        <Grid item xs={12} sm={6} key={key}>
                          <TextField
                            margin="dense"
                            label="Date"
                            name="date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={formatDate(newEntry.date)}
                            onChange={(e) =>
                              setNewEntry({ ...newEntry, date: unformatDate(e.target.value) })
                            }
                          />
                        </Grid>
                      );
                    }
                    return (
                      <Grid item xs={12} sm={6} key={key}>
                        <TextField
                          margin="dense"
                          label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          name={key}
                          fullWidth
                          value={newEntry[key] || ""}
                          onChange={(e) =>
                            setNewEntry({ ...newEntry, [key]: e.target.value })
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                <Button onClick={() => setOpenAddDialog(false)} sx={{ color: '#64748b', fontWeight: 600 }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddEntry}
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                  }}
                >
                  Save Entry
                </Button>
              </DialogActions>
            </Dialog>

            {/* Edit Entry Dialog */}
            <Dialog
              open={openEditDialog}
              onClose={() => setOpenEditDialog(false)}
              fullWidth
              maxWidth="sm"
              PaperProps={{ sx: { borderRadius: 3.5, overflow: 'hidden' } }}
            >
              <DialogTitle>Edit Customer Entry</DialogTitle>
              <DialogContent sx={{ pt: 3, bgcolor: '#ffffff' }}>
                <Grid container spacing={2}>
                  {editingEntry &&
                    Object.keys(editingEntry)
                      .filter((key) =>
                        ![
                          "id", "_id", "__v", "totalDues", "remainingDues", "duesPaid", "dues", "totalPaid",
                          "principleDues", "interestCount", "interestDues", "totalPrincipalDue", "noOfDuePaid",
                          "paidDueAmount", "profit", "docCharges", "pendingDue", "totalInterestDue", "remainingPrincipalTobePaid",
                        ].includes(key)
                      )
                      .map((key) => {
                        if (key === "accountStatus") {
                          return (
                            <Grid item xs={12} sm={6} key={key}>
                              <FormControl fullWidth margin="dense">
                                <InputLabel id="account-status-label">Account Status</InputLabel>
                                <Select
                                  labelId="account-status-label"
                                  value={editingEntry[key]}
                                  label="Account Status"
                                  onChange={handleInputChange}
                                  name={key}
                                  sx={{ borderRadius: 2.5 }}
                                >
                                  <MenuItem value="active">Active</MenuItem>
                                  <MenuItem value="closed/paid">Closed/Paid</MenuItem>
                                  <MenuItem value="NPA">NPA</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          );
                        }

                        if (key === "aadharNo1" || key === "aadharNo2") {
                          return (
                            <Grid item xs={12} sm={6} key={key}>
                              <TextField
                                margin="dense"
                                label={key === "aadharNo1" ? "Aadhar No 1" : "Aadhar No 2"}
                                name={key}
                                value={editingEntry[key] || ""}
                                inputProps={{ maxLength: 14 }}
                                onChange={(e) => {
                                  let value = e.target.value.replace(/\D/g, "");
                                  value = value.slice(0, 12);
                                  const formatted = value
                                    .replace(/(\d{4})(\d{0,4})(\d{0,4})/, (match, p1, p2, p3) =>
                                      [p1, p2, p3].filter(Boolean).join(" ")
                                    );
                                  handleInputChange({ target: { name: key, value: formatted } });
                                }}
                                fullWidth
                              />
                            </Grid>
                          );
                        }
                        if (key === "userId" || key === "password") {
                          return (
                            <Grid item xs={12} sm={6} key={key}>
                              <TextField
                                margin="dense"
                                label={key === "userId" ? "User ID" : "Password"}
                                name={key}
                                value={editingEntry[key] || ""}
                                onChange={handleInputChange}
                                fullWidth
                              />
                            </Grid>
                          );
                        }
                        return (
                          <Grid item xs={12} sm={6} key={key}>
                            <TextField
                              margin="dense"
                              label={key.charAt(0).toUpperCase() + key.slice(1)}
                              name={key}
                              value={editingEntry[key] || ""}
                              onChange={handleInputChange}
                              fullWidth
                            />
                          </Grid>
                        );
                      })}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                <Button onClick={() => setOpenEditDialog(false)} sx={{ color: '#64748b', fontWeight: 600 }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleEditSubmit}
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                  }}
                >
                  Save Changes
                </Button>
              </DialogActions>
            </Dialog>

            <DuplicateAlertDialog
              open={openDuplicateDialog}
              handleClose={handleConfirmDuplicate}
              duplicates={duplicateEntry ? [duplicateEntry] : []}
            />

            <DuplicateAlertDialog
              open={openDialog}
              handleClose={handleDuplicateDialogClose}
              duplicates={duplicates}
            />

            <Snackbar
              open={openSnackbar}
              autoHideDuration={4000}
              onClose={() => setOpenSnackbar(false)}
              message={snackbarMessage}
              sx={{
                '& .MuiSnackbarContent-root': {
                  backgroundColor: snackbarMessage.includes("Error") || snackbarMessage.includes("Failed") || snackbarMessage.includes("cancelled")
                    ? '#ef4444'
                    : '#0f766e',
                  color: '#ffffff',
                  fontWeight: 600,
                  borderRadius: 2.5,
                },
              }}
            />
          </Container>
        </Box>
      </ThemeProvider>
    </>
  );
};

export default VelavanFinance;